import React, { useRef, useMemo, useEffect } from "react";
import Adsense from "../../../Reusable/Adsense/Adsense.js";
import AnimationContainer from "../../../Reusable/AnimationContainer/AnimationContainer.js";
import SideBar from "../../../Reusable/SideBar/SideBar.js";
import MobileFooter from "./MobileFooter.js";
import FoldersContainer from "../../../Reusable/FoldersContainer/FoldersContainer.js";
import RedirectCheck from "../../../Reusable/RedirectCheck/RedirectCheck.js";
import FullScreenMessage from "../../../Reusable/FullScreenMessage/FullScreenMessage.js";
import useMediaQueries from "../../../../hooks/useMediaQueries.js";
import BackgroundCover from "../../../Reusable/BackgroundCover/BackgroundCover.js";
import HeadersContainer from "./HeadersContainer.js";
import ContentWrapper from "./ContentBody.js";
import ClosableAdsense from "../../../Reusable/Adsense/ClosableAdsense.js";
import AdBlockDetector from "../../../Structure/Page/AdBlockDetector.js";
import useMailSender from "../../../../hooks/useMailSender.js";
import UploaderProvider from "contexts/uploaderContext/uploaderContext.js";
import ReuploadPortalWrapper from "components/Reusable/Reuploader/ReuploadPortalWrapper.tsx";
import sanitizeMailId from "helpers/sanitizeMailId.ts";
import useCampaignViewCounts from "hooks/useCampaignViewCounts.ts";
import { useMail } from "contexts/mailContext/mailContext.js";
import { useNavigate } from "react-router-dom";
import { GetUsersContributersToMailApiRequest } from "api/MailsController.ts";

const GalleryComponentsWrapper = ({
  redirectTo,
  fullScreenMessage,
  setFullScreenMessage,
}) => {
  const {
    isMailAvailable,
    getIsDisplayAllowed,
    getMailStatus,
    getMailId,
    setContributorsData,
    fetchMailFiles,
    fetchFoldersData,
    setMetaDataProp,
    getMailMetaData,
  } = useMail();
  
  const { isMailSender } = useMailSender();
  const { Desktop, Mobile, mobileCondition } = useMediaQueries();
  const navigate = useNavigate();
  useCampaignViewCounts();

  const contentRef = useRef();

  const handleGetContributors = async () => {
    const mailId = sanitizeMailId(getMailId());

    if (!mailId) return;

    const res = await GetUsersContributersToMailApiRequest(mailId);
    if (res.status === "success") {
      setContributorsData(res.data);
    }
  };

  const handleFinishUpload = ({ newCrfToken, uploadedFiles }) => {
    fetchMailFiles({ reset: true });
    handleGetContributors();
    fetchFoldersData();
    setMetaDataProp("CSRFToken", newCrfToken);

    setMetaDataProp(
      "filesCount",
      getMailMetaData().filesCount + uploadedFiles.length
    );
    setMetaDataProp(
      "expectedFilesCount",
      getMailMetaData().expectedFilesCount + uploadedFiles.length
    );

    const doesContainVideo = uploadedFiles.some((file) =>
      file.file.type.includes("video")
    );
    if (doesContainVideo) {
      setMetaDataProp("DoesContainVideo", true);
      setMetaDataProp("IsVideoTranscoded", false);
    }

    const uploadedFilesSize = uploadedFiles.reduce(
      (acc, file) => acc + file.size,
      0
    );
    setMetaDataProp(
      "TotalSize",
      getMailMetaData().TotalSize + uploadedFilesSize
    );
    // cleaning url from comment info
    navigate(".", { replace: true });
  };
  
 
   return useMemo(
    () => (
      <RedirectCheck redirectTo={redirectTo}>
        <Mobile>
          <ClosableAdsense bannerPosition="download-page-preload" />
        </Mobile>
        <BackgroundCover contentRef={contentRef} />
        <HeadersContainer contentRef={contentRef} />
        <SideBar />
        <AdBlockDetector />
        <Desktop>
          <Adsense bannerPosition="download-left-side" />
        </Desktop>
        <Desktop>
          <Adsense bannerPosition="download-right-side" />
        </Desktop>
        <UploaderProvider>
          <ReuploadPortalWrapper handleFinishUpload={handleFinishUpload} />
        </UploaderProvider>
        <div className="content-holder">
          <FullScreenMessage
            message={fullScreenMessage}
            state={fullScreenMessage}
            set={setFullScreenMessage}
            isShowOnStart
          />
          {isMailAvailable() ||
          (getMailStatus() === "uploading" && isMailSender()) ? (
            <AnimationContainer
              animationOn={
                !fullScreenMessage.isShow ? getIsDisplayAllowed() : false
              }
              animationClassName="fade-in"
              containerElementClassName="gallery-content"
              isUnmountOnExit={true}
            >
              <Adsense
                bannerPosition={
                  mobileCondition ? "download-page-top" : "download-center-top"
                }
              />
              <FoldersContainer />
              <ContentWrapper />
              <Adsense bannerPosition="download-bottom-center" />
            </AnimationContainer>
          ) : null}
        </div>
        {mobileCondition && <MobileFooter />}
      </RedirectCheck>
    ),
    [
      getIsDisplayAllowed(),
      fullScreenMessage,
      isMailAvailable(),
      redirectTo,
      getMailStatus(),
      getMailMetaData().filesCount,
    ]
  );
};

export default GalleryComponentsWrapper;
